import React from 'react';
import { Query } from '@apollo/client/react/components';
import { mapAfter } from 'apollo-relay/update-query';

import LoadingProgress from 'lib/react-router/loading-progress';
import { RouteWithSearchRenderProps as RouteWithSearchProps } from 'lib/react-router/route-with-search';
import btoa from 'lib/base64/btoa';
import atob from 'lib/base64/atob';
import observe from 'lib/intersection-observer/observe';

import gaTrack from 'four-nets/lib/google-analytics/track';
import ErrorPage from 'four-nets/errors/error-page';
import FetchMoreButton, { adjacentHrefsFromCursor } from 'four-nets/ui-v2/fetch-more-button/fetch-more-button';
import OffsetPager from 'four-nets/ui-v2/pager/offset-pager';

import query, { CollectionSlugLoaderQuery, CollectionSlugLoaderQueryVariables } from './collection-slug-loader.graphql';
import { CollectionFragment } from '../collection/collection.graphql';
import defaultStyles from '../collection/collection.scss';
import InfiniteScroll from 'lib/infinite-scroll/infinite-scroll';

interface Params {
    username: string;
    slug: string;
}

interface Props {
    children: React.ComponentClass<any, any>;
}

interface State {
    loading?: boolean;
    visible: boolean;
    autoLoadActive: boolean;
}

const FIRST = 32;

class CollectionSlugLoader extends React.PureComponent<Props & RouteWithSearchProps<Params, { offset: number }>> {
    fetchMore?: (all: boolean) => Promise<any> = undefined;
    state = { autoLoadActive: false };

    onClick = () => {
        if (this.fetchMore) {
            this.fetchMore(false);

            if (!this.state.autoLoadActive) {
                this.setState({ autoLoadActive: true });
            }
        }
    };

    render() {
        const { slug } = this.props.match.params;
        const { offset } = this.props.search;

        let after: string | undefined;
        if (offset) {
            after = btoa(`arrayconnection:${parseInt(offset as any, 10) - 1}`);
        }

        return (
            <Query<CollectionSlugLoaderQuery, CollectionSlugLoaderQueryVariables>
                query={query}
                variables={{ slug, first: FIRST, after, afterRaw: after }}
                notifyOnNetworkStatusChange={true}
                fetchPolicy='cache-first'
            >
                {({ data, loading, error, fetchMore }) => {
                    const collection =
                        data && data.collections && data.collections.edges && data.collections.edges.length > 0
                            ? (data.collections.edges[0]!.node as CollectionFragment)
                            : undefined;

                    const hasNextPage =
                        collection &&
                        collection.rawImages &&
                        collection.rawImages.pageInfo &&
                        collection.rawImages.pageInfo.hasNextPage;

                    let endCursor: undefined | string | null;
                    let endCursorRaw: undefined | string | null;
                    if (collection && collection.images && collection.rawImages) {
                        endCursor = collection.images.pageInfo.endCursor;
                        endCursorRaw = collection.rawImages.pageInfo.endCursor;
                    }

                    const [endCursorHref, prevCursorHref] = adjacentHrefsFromCursor({
                        endCursor,
                        pathname: this.props.location.pathname,
                        pageSize: FIRST,
                    });

                    let nextOffset;
                    if (endCursor) {
                        nextOffset = parseInt(atob(endCursor).split(':')[1], 10) + 1;
                    } else {
                        nextOffset = offset;
                    }

                    const fetchMoreFn = hasNextPage
                        ? (all: boolean = true) => {
                              return fetchMore({
                                  variables: {
                                      after: endCursor,
                                      afterRaw: endCursorRaw,
                                      first: all ? undefined : FIRST,
                                  },
                                  updateQuery: (
                                      previousResult = {} as CollectionSlugLoaderQuery,
                                      {
                                          fetchMoreResult = {} as CollectionSlugLoaderQuery,
                                      }: { fetchMoreResult?: CollectionSlugLoaderQuery }
                                  ) => {
                                      return {
                                          __typename: 'CollectionNodeConnection',
                                          ...previousResult,
                                          ...fetchMoreResult,
                                          collections: {
                                              ...fetchMoreResult.collections,
                                              edges: [
                                                  {
                                                      ...fetchMoreResult!.collections!.edges[0]!,
                                                      node: {
                                                          ...fetchMoreResult!.collections!.edges[0]!.node!,
                                                          images: mapAfter(
                                                              previousResult!.collections!.edges[0]!.node!.images,
                                                              fetchMoreResult!.collections!.edges[0]!.node!.images
                                                          ),
                                                          rawImages: mapAfter(
                                                              previousResult!.collections!.edges[0]!.node!.rawImages,
                                                              fetchMoreResult!.collections!.edges[0]!.node!.rawImages
                                                          ),
                                                      },
                                                  },
                                              ],
                                          },
                                      };
                                  },
                              });
                          }
                        : undefined;

                    this.fetchMore = fetchMoreFn;

                    return (
                        <LoadingProgress loading={loading}>
                            <ErrorPage error={error}>
                                {collection ? (
                                    <React.Fragment>
                                        {React.createElement(this.props.children, {
                                            ...collection,
                                            fetchMore: fetchMoreFn,
                                            offset,
                                        })}
                                        <InfiniteScroll fetch={this.fetchMore} disabled={!this.state.autoLoadActive}>
                                            {hasNextPage ? (
                                                <FetchMoreButton
                                                    disabled={loading}
                                                    loading={loading}
                                                    href={endCursorHref}
                                                    prevHref={prevCursorHref}
                                                    onClick={() => {
                                                        this.onClick();
                                                        if (typeof window !== 'undefined') {
                                                            gaTrack('pageview', endCursorHref);
                                                        }
                                                    }}
                                                />
                                            ) : null}
                                        </InfiniteScroll>
                                        {hasNextPage || offset ? (
                                            <OffsetPager
                                                style={{ nav: defaultStyles.navOfOffsetPager }}
                                                pageSize={FIRST}
                                                offset={nextOffset}
                                            />
                                        ) : null}
                                    </React.Fragment>
                                ) : null}
                            </ErrorPage>
                        </LoadingProgress>
                    );
                }}
            </Query>
        );
    }
}

export default CollectionSlugLoader;
